import { useState, useEffect, useCallback } from 'react';
import { projectsAPI, projectTypesAPI } from '../utils/api';
import { PageHeader, Badge } from '../components/layout/UI';
import { useToast, apiError } from '../components/Toast';
import { format, differenceInDays, addDays, startOfMonth, endOfMonth, eachMonthOfInterval, isBefore, isAfter, parseISO } from 'date-fns';

// Blocked became Hold (phase69), and Hold is Sunglow everywhere else in the
// app. That forced two neighbouring colours to move as well: In Progress was
// amber, which Sunglow would have been mistaken for on the same chart, and
// Pending took the Sapphire that In Progress now holds — the same pairing
// MilestonesPage already uses. Skipped moves off grey so it cannot be read as
// Pending either. Red is gone: nothing here is an error.
const STATUS_COLOR = {
  'Pending':    { bar:'#94a3b8', bg:'#f1f5f9', text:'#475569' },
  'In Progress':{ bar:'#2E539F', bg:'#eef2fa', text:'#1f3d76' },
  'Hold':       { bar:'#FFC849', bg:'#fffaed', text:'#92700f' },
  'Completed':  { bar:'#10b981', bg:'#ecfdf5', text:'#065f46' },
  'Skipped':    { bar:'#b45309', bg:'#fdf5ec', text:'#7c3d06' },
};

const DAY_PX = 18; // pixels per day

function TodayLabel({ left }) {
  return (
    <div className="absolute top-0 bottom-0 z-20 pointer-events-none" style={{ left }}>
      <div className="w-0.5 h-full bg-red-500 opacity-70" />
      <div className="absolute -top-6 -translate-x-1/2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded whitespace-nowrap">
        Today
      </div>
    </div>
  );
}

export default function MilestoneTimelinePage() {
  const toast = useToast();
  const [projects,   setProjects]   = useState([]);
  const [selProject, setSelProject] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  useEffect(() => {
    projectsAPI.getAll({ limit: 0 }).then(r => setProjects(r.data || [])).catch(e => toast.error(apiError(e, 'Could not load this page.')));
  }, []);

  const load = useCallback(async () => {
    if (!selProject) return;
    setLoading(true);
    try {
      const r = await projectTypesAPI.getMilestones(selProject);
      setMilestones(r.data || []);
      setError('');
    } catch(e) { setError(e.response?.data?.message || 'Failed to load milestones'); }
    finally { setLoading(false); }
  }, [selProject]);

  useEffect(() => { load(); }, [load]);

  // Compute date range for the gantt from all milestones
  const withDates = milestones.filter(m => m.target_date);
  const today = new Date();

  const rangeStart = withDates.length
    ? startOfMonth(withDates.reduce((min, m) => {
        const d = parseISO(m.target_date);
        return isBefore(d, min) ? d : min;
      }, parseISO(withDates[0].target_date)))
    : startOfMonth(today);

  const rangeEnd = withDates.length
    ? endOfMonth(withDates.reduce((max, m) => {
        const d = parseISO(m.target_date);
        return isAfter(d, max) ? d : max;
      }, parseISO(withDates[0].target_date)))
    : endOfMonth(addDays(today, 90));

  // Extend at least 1 month past today
  const extendedEnd = isAfter(addDays(today, 30), rangeEnd)
    ? endOfMonth(addDays(today, 30)) : rangeEnd;

  const totalDays  = Math.max(1, differenceInDays(extendedEnd, rangeStart) + 1);
  const totalWidth = totalDays * DAY_PX;
  const months     = eachMonthOfInterval({ start: rangeStart, end: extendedEnd });

  const dayOffset = (date) => {
    const d = typeof date === 'string' ? parseISO(date) : date;
    return Math.max(0, differenceInDays(d, rangeStart));
  };

  const todayLeft = dayOffset(today) * DAY_PX;

  const selectedProject = projects.find(p => p.id === selProject);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Milestone Timeline"
        subtitle="Gantt-style view of project milestones and progress"
      />

      {/* Project selector */}
      <div className="card flex items-end gap-4">
        <div className="flex-1 max-w-sm">
          <label className="label">Project</label>
          <select className="select" value={selProject} onChange={e => setSelProject(e.target.value)}>
            <option value="">Select a project...</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        {selectedProject && (
          <div className="flex items-center gap-3 text-sm pb-0.5" style={{ color:'var(--text-secondary)' }}>
            <Badge value={selectedProject.status} type="status" />
            <span>{milestones.length} milestone{milestones.length !== 1 ? 's' : ''}</span>
            <span>·</span>
            <span>{milestones.filter(m=>m.status==='Completed').length} completed</span>
          </div>
        )}
      </div>

      {error && (
        <div className="p-4 rounded-xl text-sm" style={{ background:'rgba(239,68,68,.08)', color:'#ef4444', border:'1px solid rgba(239,68,68,.2)' }}>
          ⚠ {error}
        </div>
      )}
      {/* States */}
      {!selProject && (
        <div className="card text-center py-20">
          <p className="text-4xl mb-3">📅</p>
          <p className="font-semibold text-base" style={{ color:'var(--text)' }}>Select a project to view its Gantt timeline</p>
          <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>All milestones will be plotted across a time axis</p>
        </div>
      )}

      {selProject && loading && (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/>
        </div>
      )}

      {selProject && !loading && milestones.length === 0 && (
        <div className="card text-center py-20">
          <p className="text-4xl mb-3">🏁</p>
          <p className="font-semibold" style={{ color:'var(--text)' }}>No milestones yet</p>
          <p className="text-sm mt-1" style={{ color:'var(--text-secondary)' }}>Add milestones from the Milestones page</p>
        </div>
      )}

      {selProject && !loading && milestones.length > 0 && (
        <div className="card overflow-hidden p-0">
          {/* Legend */}
          <div className="px-5 py-3 border-b flex items-center gap-4 flex-wrap" style={{ borderColor:'var(--border)' }}>
            {Object.entries(STATUS_COLOR).map(([s, c]) => (
              <div key={s} className="flex items-center gap-1.5 text-xs" style={{ color:'var(--text-secondary)' }}>
                <div className="w-3 h-3 rounded-sm" style={{ background:c.bar }}/>
                {s}
              </div>
            ))}
            <div className="flex items-center gap-1.5 text-xs ml-auto" style={{ color:'var(--text-secondary)' }}>
              <div className="w-0.5 h-4 bg-red-500 opacity-70 rounded"/>
              Today ({format(today, 'MMM d')})
            </div>
          </div>

          <div className="overflow-x-auto">
            <div style={{ minWidth: totalWidth + 200 }}>

              {/* Month header row */}
              <div className="flex border-b sticky top-0 z-10" style={{ borderColor:'var(--border)', background:'var(--surface-alt)', marginLeft:200 }}>
                {months.map(month => {
                  const daysInView = Math.min(
                    differenceInDays(endOfMonth(month), month) + 1,
                    differenceInDays(extendedEnd, month) + 1
                  );
                  const w = Math.max(0, daysInView) * DAY_PX;
                  return (
                    <div key={month.toISOString()} className="border-r text-xs font-semibold px-2 py-2 flex-shrink-0"
                      style={{ width:w, borderColor:'var(--border)', color:'var(--text-secondary)' }}>
                      {format(month,'MMM yyyy')}
                    </div>
                  );
                })}
              </div>

              {/* Milestone rows */}
              <div className="relative">
                {/* Today vertical line */}
                <TodayLabel left={todayLeft + 200} />

                {/* Vertical day grid lines — every 7 days */}
                {Array.from({ length: Math.ceil(totalDays / 7) }, (_, i) => (
                  <div key={i} className="absolute top-0 bottom-0 border-l pointer-events-none"
                    style={{ left: i * 7 * DAY_PX + 200, borderColor:'var(--border)', opacity:0.3 }}/>
                ))}

                {milestones.map((m, idx) => {
                  const sc       = STATUS_COLOR[m.status] || STATUS_COLOR['Pending'];
                  const hasDates = !!m.target_date;
                  const targetOffset = hasDates ? dayOffset(m.target_date) * DAY_PX : null;
                  const pct = Math.min(100, Math.max(0, m.completion_percentage || 0));
                  const isOverdue = hasDates && isBefore(parseISO(m.target_date), today) && m.status !== 'Completed';

                  return (
                    <div key={m.id} className="flex items-center border-b hover:bg-[var(--surface-alt)] transition-colors"
                      style={{ borderColor:'var(--border)', minHeight:52 }}>

                      {/* Name column — fixed width */}
                      <div className="flex-shrink-0 px-4 py-3" style={{ width:200 }}>
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background:sc.bar }}/>
                          <p className="text-xs font-semibold truncate" style={{ color:'var(--text)' }} title={m.name}>{m.name}</p>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 pl-4">
                          <span className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                            style={{ background:sc.bg, color:sc.text, fontSize:10 }}>{m.status}</span>
                          {isOverdue && <span className="text-xs font-medium" style={{ color:'#ef4444', fontSize:10 }}>Overdue</span>}
                        </div>
                      </div>

                      {/* Gantt bar area */}
                      <div className="flex-1 relative py-3" style={{ height:52 }}>
                        {hasDates ? (
                          <>
                            {/* Bar: from left edge to target date */}
                            <div className="absolute top-1/2 -translate-y-1/2 rounded-sm flex items-center overflow-hidden"
                              style={{
                                left:    0,
                                width:   Math.max(24, targetOffset),
                                height:  24,
                                background: sc.bg,
                                border: `1.5px solid ${sc.bar}`,
                              }}>
                              {/* Progress fill */}
                              {pct > 0 && (
                                <div className="h-full rounded-sm transition-all"
                                  style={{ width:`${pct}%`, background:sc.bar, opacity:0.6 }}/>
                              )}
                            </div>

                            {/* Diamond milestone marker at target date */}
                            <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-10"
                              style={{ left: targetOffset }}>
                              <div className="w-4 h-4 rotate-45 shadow-sm"
                                style={{ background:sc.bar, border:'2px solid white' }}
                                title={`Target: ${format(parseISO(m.target_date),'MMM d, yyyy')}`}/>
                            </div>

                            {/* Date label */}
                            <div className="absolute top-1/2 -translate-y-1/2 text-xs whitespace-nowrap pl-2"
                              style={{ left: targetOffset + 12, color:'var(--text-secondary)', fontSize:10 }}>
                              {format(parseISO(m.target_date),'MMM d')}
                              {pct > 0 && <span className="ml-1" style={{ color:sc.bar }}>({pct}%)</span>}
                            </div>
                          </>
                        ) : (
                          <span className="text-xs italic pl-2" style={{ color:'var(--text-secondary)', lineHeight:'52px' }}>No date set</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bottom summary */}
              <div className="px-5 py-3 border-t flex items-center gap-6 text-xs" style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
                <span>
                  <span className="font-semibold" style={{ color:'var(--text)' }}>
                    {milestones.filter(m=>m.status==='Completed').length}/{milestones.length}
                  </span> completed
                </span>
                {withDates.length > 0 && (
                  <span>Range: {format(rangeStart,'MMM d, yyyy')} — {format(extendedEnd,'MMM d, yyyy')}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
